<template>
    <div class="stakePad" v-bind:class="{ _isQuick: isShowStakePad }">
        <div v-if="!isShowStakePad" class="stakePad-minMax">
            <span>
                {{ i18n.t('stakePad_minBet') }}: {{ minBetDisplay }} | {{ i18n.t('stakePad_maxBet') }}: {{ maxBetDisplay }}
            </span>
            <div class="growing-box" />
            <div
                v-if="!isEuroSite"
                class="stakePad-close JARVIS_BetTicket_Btn_Keypad_Close"
                v-on:click="setIsShowStakePad(true)"
            >
                <SvgIcon iconName="systemIcon_close" />
            </div>
        </div>
        <QuickStake
            v-if="!isShowStakePad"
            v-bind:currency="currency"
            v-bind:class="{ _isQuick: isShowStakePad }"
            v-on:addStake="addStake"
        />
        <div v-if="!isShowStakePad" class="stakePad-keyboard">
            <ul>
                <li
                    v-for="i in 9"
                    v-bind:key="i"
                    data-ga="keypad"
                    v-bind:data-value="i"
                    class="keybtn"
                    v-on:click="appendStake(i)"
                >
                    {{ i }}
                </li>
            </ul>
            <ul>
                <li
                    class="keybtn key0"
                    data-ga="keypad"
                    data-value="0"
                    v-on:click="append0(1)"
                >
                    0
                </li>
                <li
                    class="keybtn key00"
                    data-ga="keypad"
                    data-value="00"
                    v-on:click="append0(2)"
                >
                    00
                </li>
                <li
                    class="keybtn max"
                    data-ga="keypad"
                    data-value="Max"
                    v-on:click="max"
                >
                    {{ i18n.t('stakePad_button_max') }}
                </li>
                <li
                    class="keybtn delete JARVIS_BetTicket_Btn_Keypad_ClearStake"
                    data-ga="keypad"
                    data-value="Delete"
                    v-on:click="backspace"
                >
                    <SvgIcon iconName="systemIcon_delete" />
                </li>
                <li
                    v-if="isEuroSite"
                    class="keybtn delete JARVIS_BetTicket_Btn_Keypad_Done"
                    data-ga="keypad"
                    data-value="Done"
                    v-on:click="setIsShowStakePad(true)"
                >
                    {{ i18n.t('stakePad_button_done') }}
                </li>
            </ul>
        </div>
        <SwitchAcceptAnyOddsValue
            v-if="isShowAcceptAnyOdds"
            class="stakePad-acceptAnyOdds"
        />
    </div>
</template>

<script lang="ts">
    import { toRefs } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, watch } from 'vue';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import QuickStake from '@/components/betSlip/QuickStake.vue';
    import { calculateMaxStake } from '@/libraries/betSlip/stake';
    import useState from '@/libraries/composables/useState';
    import { getStakeInputMultiplier } from '@/libraries/helpers/stakeInputHelper';
    import { i18n } from '@/libraries/i18n';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'StakePad',
        components: {
            QuickStake,
            SwitchAcceptAnyOddsValue,
            SvgIcon,
        },
        emits: ['changeStake'],
        props: {
            balance: {
                type: Number as PropType<number | null>,
                default: null,
            },
            stake: {
                type: Number,
                default: 0,
            },
            minBet: {
                type: Number,
                default: 0,
            },
            maxBet: {
                type: Number,
                default: 0,
            },
            currency: {
                type: String,
                default: '',
            },
            price: {
                type: Number,
                default: 1,
            },
        },
        setup(props, context) {
            const stakeMaxLength = 11;
            const [shouldReplaceStake, setShouldReplaceStake] = useState(true);
            const { balance, minBet, maxBet, stake, currency, price } = toRefs(props);

            const { customerInfo } = storeToRefs(useAccountStore());
            const { formatMoney } = useAccountStore();

            const { isEuroSite } = storeToRefs(useFeatureToggleStore());
            const { isShowStakePad } = storeToRefs(useBetSlipStore());

            const multiplier = computed(() => getStakeInputMultiplier(currency.value));
            const minBetDisplay = computed(() => (minBet.value === 0 ? '' : formatMoney(minBet.value)));
            const maxBetDisplay = computed(() => (maxBet.value === 0 ? '' : formatMoney(maxBet.value)));

            const isShowAcceptAnyOdds = computed(() => !isShowStakePad.value && customerInfo.value.isAcceptAnyOdds);

            watch(stake, () => {
                setShouldReplaceStake(false);
            });

            const isStakeMoreThanMaxLength = (stake: number) => stake.toString().length > stakeMaxLength;

            function changeStakeAndFocusInput(changedStake: number) {
                window.eventEmitter.emit('focusInput');
                context.emit('changeStake', changedStake);
            }

            function addStake(n: number) {
                const changedStake = stake.value + n;
                if (Number.isNaN(changedStake)) {
                    throw new Error(`StakePad:appendStake:stake is NaN:${changedStake}`);
                }

                if (isStakeMoreThanMaxLength(changedStake)) return;
                changeStakeAndFocusInput(changedStake);
            }

            function appendStake(value: number) {
                const stakeWithoutMultiplier = shouldReplaceStake.value ? value : ((stake.value / multiplier.value) * 10 + value);
                const changedStake = stakeWithoutMultiplier * multiplier.value;
                if (Number.isNaN(changedStake)) {
                    throw new Error(`StakePad:appendStake:stake is NaN:${changedStake}`);
                }

                if (isStakeMoreThanMaxLength(changedStake)) return;
                changeStakeAndFocusInput(changedStake);
            }

            function append0(numberOfZeros: number) {
                const changedStake = stake.value * (10 ** numberOfZeros);
                if (Number.isNaN(parseFloat(changedStake.toString()))) {
                    throw new Error(`StakePad:appendStake:stake is NaN:${changedStake}`);
                }

                if (isStakeMoreThanMaxLength(changedStake)) return;
                changeStakeAndFocusInput(changedStake);
            }

            function max() {
                if (Number.isNaN(parseFloat(maxBet.value.toString()))) {
                    throw new Error(`StakePad:max:stake is NaN:${maxBet.value}`);
                }

                const maxStake = balance.value !== null
                    ? calculateMaxStake(balance.value, price.value)
                    : maxBet.value;
                changeStakeAndFocusInput(Math.min(maxBet.value, maxStake));
            }

            function backspace() {
                if (Number.isNaN(parseFloat(stake.value.toString())) || Number.isNaN(parseFloat(multiplier.value.toString()))) {
                    throw new Error(`StakePad:backspace:stake is NaN:${stake.value}:${multiplier.value}`);
                }

                const changedStake = Math.floor(stake.value / (10 * multiplier.value)) * multiplier.value;
                changeStakeAndFocusInput(changedStake);
            }

            function setIsShowStakePad(value: boolean) {
                window.eventEmitter.emit('focusInput');
                const { setIsShowStakePad } = useBetSlipStore();
                setIsShowStakePad(value);
            }

            return {
                i18n,
                customerInfo,
                stakeMaxLength,
                shouldReplaceStake,
                isEuroSite,
                isShowStakePad,
                isShowAcceptAnyOdds,
                multiplier,
                minBetDisplay,
                maxBetDisplay,
                addStake,
                appendStake,
                append0,
                max,
                backspace,
                setIsShowStakePad,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/stakePad";
</style>
